import type { Constructor, Container } from '@packages/core/container';

import { StsBackendClient, TechlineStatusCode } from '@packages/clients';

import type { WorksheetFormEntity } from '../../../worksheets/forms';
import type { TechlineCaseEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCaseSubmissionPayload } from '../helpers';
import { FetchCaseDetails, type FetchCaseDetailsResult } from './fetch-case-details';
import { UpdateCase } from './update-case';

/** Parameters for submitting a techline case for review. */
export type SubmitCaseParams = {
    /** The case number to submit. */
    caseNumber: string;

    /** The retailer number associated with the case. */
    retailerNumber: string | undefined;

    /** The current case data. */
    techlineCaseData: TechlineCaseEntity;

    /** The username making the submission. */
    username: string;

    /** The VIN associated with the case. */
    vin: string | undefined;

    /**
     * Cached worksheet submissions keyed by submission ID. Used to extract
     * answer data for the case submission payload sent to NorthStar.
     */
    worksheetSubmissionMap: Map<string, WorksheetFormEntity>;

    /**
     * When true, skip the post-submission case details re-fetch. Useful when
     * the caller is navigating away immediately and doesn't need fresh data.
     */
    shouldSkipRefresh?: boolean;
};

/**
 * Submit a techline case for review. Updates the current case data via
 * UpdateCase, transitions the status to IN_REVIEW with worksheet submissions,
 * then re-fetches the case details so callers receive fresh post-submission
 * data (updated status, server-side worksheet changes, etc.).
 */
export class SubmitCase extends StsBackendOperation {
    /**
     * Register this operation with dependencies on UpdateCase and FetchCaseDetails.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, FetchCaseDetails, UpdateCase]);
    }

    /** @param fetchCaseDetails - re-fetches case data after submission */
    private fetchCaseDetails: FetchCaseDetails;

    /** @param updateCase - persists unsaved edits before submission */
    private updateCase: UpdateCase;

    /**
     * @param client - the backend API client
     * @param fetchCaseDetails - re-fetches case data after submission
     * @param updateCase - persists unsaved edits before submission
     */
    constructor(client: StsBackendClient, fetchCaseDetails: FetchCaseDetails, updateCase: UpdateCase) {
        super(client);
        this.fetchCaseDetails = fetchCaseDetails;
        this.updateCase = updateCase;
    }

    /**
     * Update the case, submit it for review, then re-fetch the latest case data.
     *
     * @param params - the case data and identity fields
     * @returns fresh case details on success, or undefined if any step failed
     */
    async exec(params: SubmitCaseParams): Promise<FetchCaseDetailsResult | undefined> {
        const {
            caseNumber,
            retailerNumber,
            shouldSkipRefresh,
            techlineCaseData,
            username,
            vin,
            worksheetSubmissionMap,
        } = params;

        // Persist unsaved edits via the update operation
        const isUpdated = await this.updateCase.exec({ techlineCaseData, caseNumber, vin, username, retailerNumber });
        if (!isUpdated) {
            return undefined;
        }

        // Build the worksheet submissions payload with answer data from the cached map
        const submissions = buildCaseSubmissionPayload(
            techlineCaseData.WorksheetSubmissionGroups,
            worksheetSubmissionMap
        );

        // Transition the case status to IN_REVIEW
        const response = await this.client.updateCaseStatus({
            updateBody: {
                casenumber: caseNumber,
                username,
                retailernumber: retailerNumber,
                techlinecasestatus: TechlineStatusCode.IN_REVIEW,
                submissions,
            },
        });

        if (!response?.success) {
            return undefined;
        }

        // Skip re-fetch when the caller is navigating away and doesn't need fresh data
        if (shouldSkipRefresh) {
            return { caseDetails: techlineCaseData } as FetchCaseDetailsResult;
        }

        // Re-fetch case details so the caller has fresh post-submission data
        return this.fetchCaseDetails.exec({ caseNumber });
    }
}
